import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// Landing page for Supabase's password-recovery email link.
// Supabase redirects here with #access_token=... in the URL hash. supabase-js
// auto-detects the hash fragment on load and establishes a short-lived session
// (PASSWORD_RECOVERY event) that lets us call updateUser({password}) without
// the user being signed in normally.
export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'done' | 'error'
  const [errorMessage, setErrorMessage] = useState(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let mounted = true
    // If the user landed here with recovery tokens, supabase-js will fire the
    // PASSWORD_RECOVERY event once it processes the hash. Until then, the
    // form stays disabled so the user can't submit against a missing session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true)
    })
    // Also cover the case where the session is already present (direct reload).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted && session) setReady(true)
    })
    return () => { mounted = false; subscription?.unsubscribe() }
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage(null)

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.')
      return
    }

    setStatus('submitting')
    const { error } = await supabase.auth.updateUser({ password })
    if (error) {
      setStatus('error')
      setErrorMessage(error.message || 'Could not update password.')
      return
    }
    setStatus('done')
    // Sign the recovery session out so the user logs in fresh with the new password.
    await supabase.auth.signOut().catch(() => {})
    setTimeout(() => navigate('/login', { replace: true }), 1500)
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold">Set a new password</h1>
          <p className="text-sm text-muted-foreground">
            Enter a new password for your account. You'll be signed in with it on the login page.
          </p>
        </div>

        {!ready && (
          <p className="text-sm text-muted-foreground text-center">
            Verifying your reset link…
          </p>
        )}

        {ready && status !== 'done' && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="new-password">New password</label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="confirm-password">Confirm password</label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            {errorMessage && (
              <p className="text-sm text-destructive">{errorMessage}</p>
            )}
            <Button type="submit" className="w-full" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        )}

        {status === 'done' && (
          <div className="text-center space-y-3">
            <p className="text-sm">Password updated. Redirecting you to sign in…</p>
          </div>
        )}
      </div>
    </div>
  )
}
